import { Booking } from "./model";

/**
 * Progressive disclosure (unified booking page brief, 16 Sep): not every
 * section applies to every charter type, and the supplier is named differently
 * for each. A bareboat booking is a short page; a crewed one uses all of it.
 *
 * The brief also lists CA fleet as a fourth type. helm-app has no signal for it
 * — yacht.provider only says which system a yacht came from — so CA fleet is
 * not detected here rather than guessed at; crewed is the fullest disclosure
 * until an own-fleet flag exists.
 */
export type CharterKind = "bareboat" | "skippered" | "crewed";

export type CharterProfile = {
  kind: CharterKind;
  label: string;
  /** "Base / operator", "Operator + skipper", "Central agent / owner". */
  supplierLabel: string;
  /** First word, for the compact header gate ("Base paid"). */
  supplierShort: string;
  /** Crew prep, transfers and crew notes — crewed only. */
  showCharterOps: boolean;
  /** Preference sheet — skippered and crewed. */
  showPreferences: boolean;
  /** Bareboat pays the base locally, so bank details don't gate it. */
  needsBankDetails: boolean;
};

const PROFILES: Record<CharterKind, Omit<CharterProfile, "kind">> = {
  bareboat: {
    label: "Bareboat",
    supplierLabel: "Base / operator",
    supplierShort: "Base",
    // Charter ops on every type (Simon, 21 Sep); the checklist rows adapt.
    showCharterOps: true,
    showPreferences: false,
    needsBankDetails: false,
  },
  skippered: {
    label: "Skippered",
    supplierLabel: "Operator + skipper",
    supplierShort: "Operator",
    showCharterOps: true,
    showPreferences: true,
    needsBankDetails: false,
  },
  crewed: {
    label: "Crewed",
    supplierLabel: "Central agent / owner",
    supplierShort: "Supplier",
    showCharterOps: true,
    showPreferences: true,
    needsBankDetails: true,
  },
};

export function charterKind(booking: Booking): CharterKind {
  const raw = String(booking?.offer?.charterType ?? (booking?.importDetails as any)?.charterType ?? "").toLowerCase();
  if (/crew|gulet|hostess|cabin/.test(raw)) return "crewed";
  if (/skipper/.test(raw)) return "skippered";
  if (raw) return "bareboat";
  // No charter type on the offer (common on platform bookings): fall back to
  // the yacht's own type rather than silently assuming bareboat, which hid the
  // Charter ops and Preferences tabs on crewed and skippered charters.
  const yt = String((booking?.offerYacht?.yacht as any)?.type ?? "").toLowerCase();
  if (yt === "crewed") return "crewed";
  if (yt === "skippered-only" || yt === "skippered") return "skippered";
  return "bareboat";
}

export function charterProfile(booking: Booking): CharterProfile {
  const kind = charterKind(booking);
  return { kind, ...PROFILES[kind] };
}

/** The disclosure strip under the hero: what this layout shows and hides. */
export function disclosureFor(kind: CharterKind): string {
  return kind === "bareboat"
    ? "lean: money + docs, no crew ops or owner payout schedule."
    : kind === "skippered"
      ? "skipper card and preference sheet shown; owner settlement hidden."
      : "crew notes and owner settlement shown; bareboat hides these.";
}

/** The review only exists once the charter has started (Simon, 17 Sep). */
export function charterStarted(booking: Booking): boolean {
  const raw = booking?.offerYacht?.startDate ?? booking?.offer?.charterStartDate ?? (booking?.importDetails as any)?.startDate;
  if (!raw) return false;
  const d = new Date(raw);
  return !Number.isNaN(d.getTime()) && d <= new Date();
}

/**
 * Tabs (v14 brief, 17 Sep): the body is tabbed like the ops Deal page and reads
 * ?tab=. Legacy aliases keep old links working — the ops page's brief /
 * documents / checklist, and this page's own former section anchors.
 */
export const TAB_ALIASES: Record<string, string> = {
  brief: "overview",
  documents: "contract",
  checklist: "ops",
  payments: "money",
  xero: "money",
  "supplier-bank": "contract",
  "charter-ops": "ops",
  preferences: "ops",
  "equipment-and-extras": "overview",
  extras: "overview",
  operator: "money",
};
export const resolveTab = (id: string | null | undefined) => (id ? TAB_ALIASES[id] ?? id : "overview");

/** Tabs in order, filtered to the ones this charter type uses. */
export function tabsFor(booking: Booking): { id: string; label: string }[] {
  const p = charterProfile(booking);
  return [
    { id: "overview", label: "Overview" },
    { id: "money", label: "Money" },
    { id: "commission", label: "Commission" },
    { id: "contract", label: "Contract" },
    ...(p.showCharterOps || p.showPreferences ? [{ id: "ops", label: "Charter ops" }] : []),
    { id: "comms", label: "Comms" },
    { id: "boarding", label: "Boarding" },
    ...(charterStarted(booking) ? [{ id: "review", label: "Review" }] : []),
    { id: "timeline", label: "Timeline" },
  ];
}
